<template>
  <b-modal
    v-model="isOpen"
    size="xl"
    centered
    no-close-on-backdrop
    no-enforce-focus
    hide-header-close
    hide-footer
    content-class="furnace-history-modal"
  >
    <!-- Modal Header Customizado -->
    <template #modal-header>
      <div class="d-flex align-items-center justify-content-between w-100">
        <div class="d-flex align-items-center">
          <div class="header-icon-circle mr-1">
            <feather-icon icon="ClockIcon" size="24" />
          </div>
          <div>
            <h4 class="modal-title-custom mb-0">Histórico de Fornadas</h4>
            <span class="modal-subtitle-custom text-muted">
              Consulte as fornadas concluídas e registros de apontamento dos fornos de envelhecimento
            </span>
          </div>
        </div>
        <button type="button" class="close-btn-custom" @click="closeModal">
          <feather-icon icon="XIcon" size="20" />
        </button>
      </div>
    </template>

    <div class="history-modal-body">
      <!-- Filtros e KPIs -->
      <div class="filters-row mb-2">
        <div class="row align-items-center">
          <!-- Filtro de Forno -->
          <div class="col-12 col-md-4 mb-1 mb-md-0">
            <label class="filter-label">Filtrar por Forno:</label>
            <b-form-select
              v-model="selectedFurnaceId"
              :options="furnaceOptions"
              class="form-control-custom"
              @change="onFilterChange"
            />
          </div>

          <!-- Filtro de Período -->
          <div class="col-12 col-md-4 mb-1 mb-md-0">
            <label class="filter-label">Período:</label>
            <b-form-select
              v-model="selectedPeriod"
              :options="periodOptions"
              class="form-control-custom"
              @change="onFilterChange"
            />
          </div>

          <!-- Botão Atualizar -->
          <div class="col-12 col-md-4 d-flex align-items-end justify-content-md-end mt-1 mt-md-0">
            <button
              type="button"
              class="btn-refresh d-flex align-items-center"
              :disabled="loading"
              @click="fetchHistory(1)"
            >
              <feather-icon icon="RefreshCwIcon" size="14" :class="{ 'spin': loading }" class="mr-50" />
              <span>Atualizar</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Resumo KPI -->
      <div class="kpi-grid mb-2">
        <div class="kpi-card">
          <div class="kpi-icon-wrapper kpi-orange">
            <feather-icon icon="LayersIcon" size="20" />
          </div>
          <div class="kpi-content">
            <span class="kpi-title">Fornadas Realizadas</span>
            <span class="kpi-value">{{ totalCharges }}</span>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon-wrapper kpi-blue">
            <feather-icon icon="PackageIcon" size="20" />
          </div>
          <div class="kpi-content">
            <span class="kpi-title">Total de Cestos</span>
            <span class="kpi-value">{{ totalBasketsProcessed }}</span>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon-wrapper kpi-green">
            <feather-icon icon="CheckCircleIcon" size="20" />
          </div>
          <div class="kpi-content">
            <span class="kpi-title">Total de Barras</span>
            <span class="kpi-value">{{ totalBarsProcessed }}</span>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon-wrapper kpi-purple">
            <feather-icon icon="HourglassIcon" size="20" />
          </div>
          <div class="kpi-content">
            <span class="kpi-title">Duração Média</span>
            <span class="kpi-value">{{ averageDurationFormatted }}</span>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading && charges.length === 0" class="text-center py-5">
        <b-spinner variant="warning" label="Carregando..." class="mb-1" />
        <p class="text-muted font-weight-bold">Carregando histórico de fornadas...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loading && charges.length === 0" class="empty-state text-center py-5">
        <feather-icon icon="InboxIcon" size="48" class="text-muted mb-1 opacity-5" />
        <h5 class="text-muted font-weight-bold">Nenhuma fornada encontrada</h5>
        <p class="text-muted small mb-0">Não foram localizados registros de fornadas para os filtros selecionados.</p>
      </div>

      <!-- Lista de Cargas Históricas -->
      <div v-else class="charges-history-list">
        <div
          v-for="charge in charges"
          :key="charge.id"
          class="charge-history-card mb-1"
        >
          <!-- Cabeçalho do Card da Carga -->
          <div class="charge-header d-flex flex-wrap align-items-center justify-content-between">
            <div class="d-flex align-items-center flex-wrap mb-50 mb-md-0">
              <!-- Nome do Forno -->
              <span class="furnace-pill mr-1">
                <feather-icon icon="ZapIcon" size="14" class="mr-25 text-warning" />
                {{ charge.furnaceName }}
              </span>

              <!-- Código da Carga -->
              <strong class="charge-code mr-1">{{ charge.chargeCode }}</strong>

              <!-- Badge de Status -->
              <span
                class="badge badge-status mr-1"
                :style="{ backgroundColor: charge.statusColor || '#28c76f', color: '#fff' }"
              >
                {{ charge.status }}
              </span>
            </div>

            <!-- Dados do Operador e Duração -->
            <div class="d-flex align-items-center flex-wrap">
              <!-- Operador -->
              <div class="operator-badge mr-1 d-flex align-items-center">
                <div class="avatar-circle mr-50">
                  <img v-if="charge.userPhoto" :src="charge.userPhoto" alt="Avatar" />
                  <span v-else>{{ avatarText(charge.userName) }}</span>
                </div>
                <span class="operator-name text-dark font-weight-600">{{ charge.userName || 'Operador' }}</span>
              </div>

              <!-- Duração -->
              <div class="duration-badge mr-1">
                <feather-icon icon="ClockIcon" size="13" class="mr-25 text-muted" />
                <span class="text-muted font-weight-600">{{ charge.durationFormatted || '-' }}</span>
              </div>

              <!-- Botão Expandir / Recolher Detalhes -->
              <button
                type="button"
                class="btn-toggle-expand"
                @click="toggleExpand(charge.id)"
              >
                <span class="mr-25">{{ isExpanded(charge.id) ? 'Recolher' : 'Ver Cestos' }}</span>
                <feather-icon :icon="isExpanded(charge.id) ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
              </button>
            </div>
          </div>

          <!-- Timeline e Resumo Rápido -->
          <div class="charge-meta-row d-flex flex-wrap align-items-center justify-content-between px-1 py-50 bg-light-soft border-top">
            <div class="d-flex align-items-center flex-wrap small text-muted">
              <span class="mr-1">
                <strong>Início:</strong> {{ formatDateTime(charge.startedAt) }}
              </span>
              <span v-if="charge.finishedAt" class="mr-1">
                <strong>Término:</strong> {{ formatDateTime(charge.finishedAt) }}
              </span>
              <span v-if="charge.observation" class="mr-1 text-dark">
                <strong>Obs:</strong> {{ charge.observation }}
              </span>
            </div>
            <div class="small font-weight-bold text-dark">
              <span class="mr-1">{{ charge.totalBaskets }} cestos</span>
              <span class="text-warning">{{ charge.totalBars }} barras</span>
            </div>
          </div>

          <!-- Tabela de Cestos da Carga (Expansível) -->
          <b-collapse :id="`collapse-${charge.id}`" :visible="isExpanded(charge.id)">
            <div class="baskets-detail-table p-1">
              <div v-if="charge.baskets && charge.baskets.length > 0" class="table-responsive">
                <table class="table table-sm table-baskets mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Cesto</th>
                      <th>OP</th>
                      <th>Produto / Perfil</th>
                      <th>Liga / Têmpera</th>
                      <th class="text-right">Barras</th>
                      <th class="text-right">Tamanho</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(b, bIdx) in charge.baskets" :key="b.id || bIdx">
                      <td class="font-weight-bold text-muted">{{ bIdx + 1 }}</td>
                      <td>
                        <strong class="text-warning font-family-monospace">{{ b.basketCode }}</strong>
                      </td>
                      <td>
                        <span v-if="b.orderNumber" class="badge badge-light-primary font-weight-bold">
                          OP {{ b.orderNumber }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="product-col" :title="b.materialName || b.product">
                        {{ b.materialName || b.product || 'Material Padrão' }}
                      </td>
                      <td>
                        <span class="badge badge-light-secondary font-weight-bold mr-25">
                          {{ b.alloy || '6060' }}
                        </span>
                        <span v-if="b.temper" class="badge badge-light-info font-weight-bold">
                          {{ b.temper }}
                        </span>
                      </td>
                      <td class="text-right font-weight-bold text-dark">
                        {{ b.barQuantity }} un
                      </td>
                      <td class="text-right text-muted">
                        {{ formatBarSize(b.barSizeMm) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center py-2 text-muted small">
                Nenhum detalhe de cesto registrado para esta fornada.
              </div>
            </div>
          </b-collapse>
        </div>
      </div>

      <!-- Paginação -->
      <div v-if="totalPages > 1" class="d-flex align-items-center justify-content-between mt-2 pt-1 border-top">
        <span class="text-muted small">
          Exibindo {{ charges.length }} de {{ totalCharges }} fornadas
        </span>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCharges"
          :per-page="pageSize"
          align="right"
          size="sm"
          class="mb-0"
          @change="onPageChange"
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import { avatarText } from "@core/utils/filter";
import moment from 'moment';
import {
  BModal,
  BSpinner,
  BFormSelect,
  BCollapse,
  BPagination
} from 'bootstrap-vue';
import { getFurnaceChargesHistory } from '../extrusionApi';

export default {
  name: 'FurnaceHistoryModal',
  components: {
    BModal,
    BSpinner,
    BFormSelect,
    BCollapse,
    BPagination
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    furnaces: {
      type: Array,
      default: () => []
    },
    siteId: {
      type: [Number, String],
      default: 1
    }
  },
  data() {
    return {
      loading: false,
      charges: [],
      totalCharges: 0,
      currentPage: 1,
      pageSize: 15,
      totalPages: 1,
      selectedFurnaceId: 0,
      selectedPeriod: '7days',
      expandedChargeIds: {},
      periodOptions: [
        { value: 'today', text: 'Hoje' },
        { value: '7days', text: 'Últimos 7 dias' },
        { value: '30days', text: 'Últimos 30 dias' },
        { value: 'all', text: 'Todas' }
      ]
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.open;
      },
      set(val) {
        this.$emit('update:open', val);
      }
    },
    furnaceOptions() {
      const opts = [{ value: 0, text: 'Todos os Fornos' }];
      if (Array.isArray(this.furnaces)) {
        for (const f of this.furnaces) {
          opts.push({
            value: f.furnaceId,
            text: f.furnaceName || `Forno ${f.furnaceId}`
          });
        }
      }
      return opts;
    },
    totalBasketsProcessed() {
      if (!this.charges || this.charges.length === 0) return 0;
      return this.charges.reduce((sum, c) => sum + (c.totalBaskets || 0), 0);
    },
    totalBarsProcessed() {
      if (!this.charges || this.charges.length === 0) return 0;
      return this.charges.reduce((sum, c) => sum + (c.totalBars || 0), 0);
    },
    averageDurationFormatted() {
      if (!this.charges || this.charges.length === 0) return '-';
      const chargesWithDuration = this.charges.filter(c => c.durationSeconds > 0);
      if (chargesWithDuration.length === 0) return '-';
      const totalSecs = chargesWithDuration.reduce((sum, c) => sum + c.durationSeconds, 0);
      const avgSecs = Math.round(totalSecs / chargesWithDuration.length);
      const hours = Math.floor(avgSecs / 3600);
      const mins = Math.floor((avgSecs % 3600) / 60);
      return `${hours}h ${mins.toString().padStart(2, '0')}m`;
    }
  },
  watch: {
    open(val) {
      if (val) {
        this.fetchHistory(1);
      }
    }
  },
  methods: {
    closeModal() {
      this.isOpen = false;
    },
    onFilterChange() {
      this.fetchHistory(1);
    },
    onPageChange(page) {
      this.fetchHistory(page);
    },
    toggleExpand(chargeId) {
      this.$set(this.expandedChargeIds, chargeId, !this.expandedChargeIds[chargeId]);
    },
    isExpanded(chargeId) {
      return !!this.expandedChargeIds[chargeId];
    },
    avatarText,
    formatDateTime(dt) {
      if (!dt) return '-';
      return moment(dt).format('DD/MM/YYYY HH:mm');
    },
    formatBarSize(size) {
      if (!size) return '-';
      let num = Number(size);
      if (isNaN(num) || num <= 0) return '-';
      if (num <= 50) num *= 1000;
      return `${num.toFixed(0)} mm`;
    },
    async fetchHistory(page = 1) {
      this.loading = true;
      this.currentPage = page;
      try {
        let startDate = null;
        let endDate = null;
        const now = moment();

        if (this.selectedPeriod === 'today') {
          startDate = now.clone().startOf('day').toISOString();
          endDate = now.clone().endOf('day').toISOString();
        } else if (this.selectedPeriod === '7days') {
          startDate = now.clone().subtract(7, 'days').startOf('day').toISOString();
          endDate = now.clone().endOf('day').toISOString();
        } else if (this.selectedPeriod === '30days') {
          startDate = now.clone().subtract(30, 'days').startOf('day').toISOString();
          endDate = now.clone().endOf('day').toISOString();
        }

        const params = {
          siteId: this.siteId,
          furnaceId: this.selectedFurnaceId > 0 ? this.selectedFurnaceId : null,
          startDate,
          endDate,
          page: this.currentPage,
          pageSize: this.pageSize
        };

        const res = await getFurnaceChargesHistory(params);
        const data = res.data?.data || res.data;
        if (data) {
          this.charges = data.items || [];
          this.totalCharges = data.totalCount || 0;
          this.totalPages = data.totalPages || 1;
        } else {
          this.charges = [];
          this.totalCharges = 0;
          this.totalPages = 1;
        }
      } catch (err) {
        console.error('Erro ao buscar histórico de fornadas:', err);
        this.charges = [];
        this.totalCharges = 0;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss">
.furnace-history-modal {
  border-radius: 12px;
  overflow: hidden;

  .modal-header {
    background: #fff;
    padding: 18px 24px;
    border-bottom: 1px solid #ebe6e2;
  }

  .header-icon-circle {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: #ffede2;
    color: #974900;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-title-custom {
    font-family: 'Raleway', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #332f2e;
  }

  .modal-subtitle-custom {
    font-size: 13px;
  }

  .close-btn-custom {
    background: transparent;
    border: none;
    color: #999;
    padding: 6px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #f0edea;
      color: #333;
    }
  }

  .history-modal-body {
    padding: 20px 24px;
    background: #fbfaf9;
    max-height: 75vh;
    overflow-y: auto;
  }

  /* Filters */
  .filter-label {
    font-size: 12px;
    font-weight: 700;
    color: #555;
    margin-bottom: 4px;
    display: block;
  }

  .form-control-custom {
    border-radius: 8px;
    border: 1px solid #ddd;
    font-size: 13px;
    height: 38px;
    background-color: #fff;

    &:focus {
      border-color: #974900;
      box-shadow: 0 0 0 2px rgba(151, 73, 0, 0.15);
    }
  }

  .btn-refresh {
    height: 38px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 13px;
    padding: 0 16px;
    background: #ffffff;
    border: 1px solid #974900;
    color: #974900;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    outline: none;

    .feather {
      color: #974900;
    }

    &:hover:not(:disabled) {
      background: #ffede2;
      border-color: #974900;
      color: #974900;
    }

    &:active:not(:disabled),
    &:focus:not(:disabled) {
      background: #ffdbc4;
      border-color: #7a3b00;
      color: #7a3b00;
      box-shadow: 0 0 0 2px rgba(151, 73, 0, 0.18);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  /* KPI Grid */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
  }

  .kpi-card {
    background: #fff;
    border: 1px solid #ebe6e2;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;

    .kpi-icon-wrapper {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      flex-shrink: 0;

      &.kpi-orange { background: #ffede2; color: #974900; }
      &.kpi-blue { background: #eef2ff; color: #4361ee; }
      &.kpi-green { background: #e8faf0; color: #28c76f; }
      &.kpi-purple { background: #f3e8ff; color: #7367f0; }
    }

    .kpi-content {
      display: flex;
      flex-direction: column;

      .kpi-title {
        font-size: 11px;
        color: #777;
        font-weight: 600;
        text-transform: uppercase;
      }

      .kpi-value {
        font-size: 18px;
        font-weight: 700;
        color: #222;
      }
    }
  }

  /* Charge Cards */
  .charge-history-card {
    background: #fff;
    border: 1px solid #ebe6e2;
    border-radius: 10px;
    overflow: hidden;
    transition: all 0.2s ease-in-out;

    &:hover {
      border-color: #d6ccc5;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
    }

    .charge-header {
      padding: 12px 16px;
    }

    .furnace-pill {
      background: #fdf6ec;
      color: #b88230;
      border: 1px solid #faecd8;
      border-radius: 6px;
      padding: 4px 10px;
      font-size: 13px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
    }

    .charge-code {
      font-family: 'SFMono-Regular', Consolas, monospace;
      font-size: 14px;
      color: #333;
    }

    .badge-status {
      padding: 5px 10px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .operator-badge {
      .avatar-circle {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: #e2e8f0;
        color: #4a5568;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .operator-name {
        font-size: 13px;
      }
    }

    .duration-badge {
      background: #f1f5f9;
      border-radius: 6px;
      padding: 4px 8px;
      font-size: 12px;
      display: inline-flex;
      align-items: center;
    }

    .btn-toggle-expand {
      background: transparent;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 4px 10px;
      font-size: 12px;
      font-weight: 600;
      color: #4a5568;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      transition: all 0.2s;

      &:hover {
        background: #f8fafc;
        border-color: #cbd5e1;
      }
    }

    .bg-light-soft {
      background: #faf8f6;
    }

    .table-baskets {
      thead th {
        font-size: 11px;
        text-transform: uppercase;
        color: #718096;
        border-top: none;
        border-bottom: 1px solid #e2e8f0;
        padding: 8px 10px;
      }

      tbody td {
        padding: 8px 10px;
        font-size: 13px;
        vertical-align: middle;
        border-top: 1px solid #f1f5f9;
      }

      .product-col {
        max-width: 250px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}
</style>

